import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BbitString, IBbitI18n } from '@bbitgmbh/bbit.utils';
import { BbitWebShopAlgoliaService, BbitWebShopService, IBbitWebShopCategory } from '@bbitgmbh/webshop.sdk';
import { BehaviorSubject, map, Observable } from 'rxjs';
// import { NavOverlayService } from '../../nav/nav-overlay/nav-overlay.service';

export interface IMenuItem {
  _id: string;
  text: IBbitI18n;
  splitted: string[];
  count: number;
  facet: string;
  facetValue: string;
  children: IMenuItem[];
  category: IBbitWebShopCategory;
}

@Injectable({
  providedIn: 'root',
})
export class HierarchicalMenuService {
  private _algolia = inject(BbitWebShopAlgoliaService);
  private _router = inject(Router);
  private _webShop = inject(BbitWebShopService);
  // private _navOverlay = inject(NavOverlayService);

  public items = new BehaviorSubject<IMenuItem[]>([]);

  private _facets = ['categories.lvl0', 'categories.lvl1'];
  private _availableFacets = new BehaviorSubject<Record<string, Record<string, number>> | undefined>(undefined);

  constructor() {
    this._algolia.observeAllCategoryFacets().subscribe((facets) => {
      this._availableFacets.next(facets);
    });

    this._availableFacets.asObservable().subscribe(() => {
      this._createItems();
    });
  }

  private _createItems(): void {
    if (!this._availableFacets.value || !this._facets) {
      return;
    }

    const allCategories = this._webShop.getCategories();

    const newItems: IMenuItem[] = [];
    for (const facet of this._facets) {
      const data = this._availableFacets.value[facet];

      const toAdd = Object.keys(data).map((key: string) => {
        const splitted = key.split(' > ');
        const _id = BbitString.stripBeginningHashtag(splitted[splitted.length - 1]);
        const category = allCategories.find(
          (c) => BbitString.ensureBeginningHashtag(c._id) === BbitString.ensureBeginningHashtag(_id),
        );
        return {
          _id,
          text: { object: category?.designation },
          category,
          count: data[key],
          splitted,
          facet,
          facetValue: key,
          children: [],
        } as IMenuItem;
      });
      newItems.push(...toAdd);
    }

    const recursiveFind = (items: IMenuItem[], toFind: IMenuItem): IMenuItem | undefined => {
      for (const item of items) {
        const found = item.splitted.join(' > ') === toFind.splitted.slice(0, -1).join(' > ');
        if (found) {
          return item;
        }

        const foundItem = recursiveFind(item.children, toFind);
        if (foundItem) {
          return foundItem;
        }
      }

      return undefined;
    };

    const items: IMenuItem[] = [];
    for (const o of newItems) {
      if (o.splitted.length === 1) {
        items.push(o);
      } else {
        // find parent
        const found = recursiveFind(items, o);
        if (found) {
          found.children.push(o);
        }
      }
    }

    this.items.next(items);
  }

  async filter(item: IMenuItem) {
    this._algolia.clearAttributeFilters();
    this._algolia.setCategoryFilter(item.facet, item.facetValue);
    // if (item.children.length === 0) {
    //   this._navOverlay.close();
    // }
    if (!this._router.url?.startsWith('/shop/items')) {
      this._router.navigate(['/shop/items'], { queryParamsHandling: 'merge' });
    }
  }

  public observeIsFacetFilterActive(facet: string, value: string): Observable<boolean> {
    return this._algolia.observeCategoryFilters().pipe(
      map((facets) => {
        return facets?.includes(`${facet}:${value}`);
      }),
    );
  }

  public observeItems(): Observable<IMenuItem[]> {
    return this.items.asObservable();
  }
}
